import { ErrorBoundary, LoadingSpinner } from 'components'
import { MotionGlobalConfig } from 'framer-motion'
import { PusherProvider } from 'modules/core'
import { headersResolver } from 'modules/core/services/api'
import { installStaleBundleGuard } from 'modules/core/services/stale-bundle'
import { OpenAPI } from 'openapi'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { initializeErrorReporting, reactErrorHandler } from 'services/error-reporting'
import { initializeErrorTracking } from 'services/error-tracking'
import { captureAttributionFromUrl, initializeFunnelFighters } from 'services/funnel-fighters'
import { initializeAnalytics } from 'services/mixpanel'
import store, { persistor } from 'store'
import { App } from './App'
import './index.css'

// Test backdoor: lets Playwright disable framer-motion entirely (including
// layoutId transitions) so screenshots land on steady-state pixels. Production
// never sets this flag — the onboarding nightly spec sets it via addInitScript.
if ((window as unknown as { __TAKA_DISABLE_MOTION__?: boolean }).__TAKA_DISABLE_MOTION__) {
  MotionGlobalConfig.skipAnimations = true
}

// Initialize external services
OpenAPI.BASE = import.meta.env.VITE_API_URL
OpenAPI.HEADERS = headersResolver
// Cookie-based auth requires both `WITH_CREDENTIALS` and `CREDENTIALS: 'include'`.
// Without this, the browser silently drops the refresh-token cookie on cross-site
// fetches and the app boots into a logged-out state for everyone.
OpenAPI.WITH_CREDENTIALS = true
OpenAPI.CREDENTIALS = 'include'

// Analytics first: it initializes PostHog, which error reporting ships through
initializeAnalytics()
initializeErrorReporting()
// Before React mounts, so the first lazy route of the page is covered too: a
// tab on a pre-deploy bundle reloads itself once instead of dying on a 404'd
// chunk (error reporting needs to be up first so the reload is counted).
installStaleBundleGuard()
initializeErrorTracking()
initializeFunnelFighters()
// Persist any attribution params forwarded to the app URL (e.g. a referral
// `?ref=CODE` from getAppUrl) into the shared cookie now — before any OAuth
// redirect strips the URL — so signup's withReferralCode reads it reliably.
captureAttributionFromUrl()

createRoot(document.getElementById('root')!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: reactErrorHandler('react.onUncaughtError', (error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack)
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: reactErrorHandler('react.onCaughtError'),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: reactErrorHandler('react.onRecoverableError'),
}).render(
  <StrictMode>
    <ErrorBoundary showOopsDialog={false}>
      <Provider store={store}>
        <PersistGate
          loading={
            <div className="flex min-h-screen items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          }
          persistor={persistor}
        >
          <PusherProvider>
            <App />
          </PusherProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  </StrictMode>
)
